import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useSignOutMutation } from 'api/main';
import { useSendRegulatoryDocumentsMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { useDiciUrl } from 'utils/hooks';
import { isStatusOk } from 'utils/helpers';
import { fundTypes } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import TunnelStep from '../common/TunnelStep';
import { SubmitButton, ConfirmDialog } from 'components';
import { Box, Link, Typography } from '@mui/material';
import logo from 'assets/logo/logo_peqan_icone_rvb.png';

const linkSx = theme => ({
    color: theme.palette.common.black,
    textDecorationColor: theme.palette.common.black,
    '&:hover': {
        textDecorationColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
    },
});
const paragraphSx = {
    fontSize: '20px',
    color: 'common.black',
    '&:hover': { color: 'primary.main' },
};

const getTunnelStepSubTitle = (isSLP, isFPS) =>
    isSLP
        ? texts.ALLOW_TO_SUBSCRIBE_COMMENT_SLP
        : isFPS
        ? texts.ALLOW_TO_SUBSCRIBE_COMMENT_FPS
        : texts.ALLOW_TO_SUBSCRIBE_COMMENT;

const getFundRegulationsText = (isSLP, isFPS) =>
    isSLP
        ? texts.FUND_REGULATIONS_SLP
        : isFPS
        ? texts.FUND_REGULATIONS_FPS_2
        : texts.FUND_REGULATIONS;

const RegulatoryDocuments = ({ handleNextStep, userStep }) => {
    const [signOut, { isLoading: signOutLoading }] = useSignOutMutation();
    const [sendRegulatoryDocuments, { isLoading: buttonLoader }] =
        useSendRegulatoryDocumentsMutation();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const fundRegulationUrl = tunnelSubscription?.investmentFund?.fundRegulation?.url;
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFPS = tunnelSubscription?.investmentFund?.type === fundTypes.FPS;

    const diciUrl = useDiciUrl(tunnelSubscription, true);

    const handleSubmit = useCallback(() => {
        sendRegulatoryDocuments(() => handleNextStep(true));
    }, [sendRegulatoryDocuments, handleNextStep]);

    const handleSignOut = useCallback(() => {
        signOut(true);
    }, [signOut]);

    return (
        <TunnelStep
            title={texts.DOCUMENTS_REGLEMENTAIRES}
            subtitle={getTunnelStepSubTitle(isSLP, isFPS)}
            maxWidth={950}
        >
            <Box display="flex" justifyContent="space-evenly" mb={15} flexWrap="wrap">
                <Box display="flex" flexDirection="column" alignItems="center">
                    <img src={logo} alt="Logo" width="250px" height="250px" />
                    <Link
                        href={fundRegulationUrl}
                        download
                        target="_blank"
                        rel="noreferrer"
                        sx={linkSx}
                    >
                        <Typography gutterBottom sx={paragraphSx}>
                            {getFundRegulationsText(isSLP, isFPS)}
                        </Typography>
                    </Link>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <img src={logo} alt="Logo" width="250px" height="250px" />
                    <Link href={diciUrl} download target="_blank" rel="noreferrer" sx={linkSx}>
                        <Typography gutterBottom sx={paragraphSx}>
                            {texts.DOCUMENT_INFORMATION_CLE}
                        </Typography>
                    </Link>
                </Box>
            </Box>
            <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="space-around"
                width="75%"
                margin="auto"
                gap={2}
                sx={theme => ({
                    [theme.breakpoints.down('md')]: { width: 'auto' },
                    [theme.breakpoints.down('sm')]: {
                        width: '100%',
                        margin: 'auto',
                        display: 'block',
                    },
                })}
            >
                <ConfirmDialog
                    open={confirmOpen}
                    handleConfirm={handleSignOut}
                    handleClose={() => setConfirmOpen(false)}
                    loading={signOutLoading}
                    title={texts.DEJA_QUITTER}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                >
                    <React.Fragment>
                        <Typography>{texts.POUVEZ_REVENIR}</Typography>
                        <Typography>{texts.VOUS_RECONNECTER}</Typography>
                    </React.Fragment>
                </ConfirmDialog>
                <SubmitButton
                    onClick={() => setConfirmOpen(true)}
                    text={texts.CONTINUER_PLUS_TARD}
                    color="secondary"
                    sx={theme => ({ [theme.breakpoints.up('xs')]: { width: 'auto' } })}
                    buttonSx={{ backgroundColor: 'grey.300' }}
                />
                <SubmitButton
                    onClick={isResubmit && hasStatusOk ? handleNextStep : handleSubmit}
                    loading={buttonLoader}
                    disabled={buttonLoader}
                    text={texts.SUIVANT}
                    sx={theme => ({ [theme.breakpoints.up('xs')]: { width: 'auto' } })}
                />
            </Box>
        </TunnelStep>
    );
};

RegulatoryDocuments.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default RegulatoryDocuments;
